import BaseData from '../../BaseData'
import unitConverter from '../../../utils/unitConverter'
import CSVDownloader from '../../../utils/CSVDownloader'

export default {
  extends: BaseData,
  data () {
    return {
      query: {
        siteIds: [],
        factorIds: [],
        isAuditCompleted: false, // finished
        granularity: 'day'
      },
      unitProp: 'displayUnit',
      headerColumns: [],
      dataList: []
    }
  },
  computed: {
    filterSites () {
      return this.sites.reduce((list, site) => {
        let siteDevices = site.siteDevices.filter(siteDevice => siteDevice.siteDeviceFactors.length > 0)

        if (siteDevices.length > 0) {
          list.push({
            id: site.id,
            name: site.name,
            siteDevices
          })
        }

        return list
      }, [])
    },
    filterSiteMap () {
      return this.filterSites.reduce((map, site) => {
        map[site.id] = site
        return map
      }, {})
    },
    formatFactorGroupMap () {
      return this.factorGroups.map(factorGroup => ({
        id: factorGroup.id,
        name: factorGroup.name,
        parentId: factorGroup.parentId
      })).reduce((map, factorGroup) => {
        map[factorGroup.id] = factorGroup
        return map
      }, {})
    },
    filterFactorMap () {
      let {factorMap} = this
      return this.filterSites.reduce((map, site) => site.siteDevices.reduce((map, siteDevice) => siteDevice.siteDeviceFactors.reduce((map, siteDeviceFactor) => {
        let {factorId} = siteDeviceFactor

        if (!map[factorId]) {
          let factor = factorMap[factorId]
          map[factorId] = {
            id: factorId,
            name: factor.name,
            parentId: factor.parentId
          }
        }

        return map
      }, map), map), {})
    },
    filterFactors () {
      return Object.values(this.filterFactorMap)
    },
    factorTree () {
      let {filterFactors} = this
      let factorGroupMap = JSON.parse(JSON.stringify(this.formatFactorGroupMap))
      let roots = []

      for (let i = 0; i < filterFactors.length; i++) {
        let factor = filterFactors[i]
        let {parentId} = factor
        let parent = factorGroupMap[parentId]

        if (parent && parentId !== 'm30000') {
          let {children} = parent

          if (!Array.isArray(children)) {
            children = parent.children = []
            filterFactors.splice(i + 1, 0, parent)
          }

          children.push(factor)
        } else {
          roots.push(factor)
        }
      }

      return roots
    },
    canNotSearch () {
      let {query} = this
      return query.siteIds.length === 0 || query.factorIds.length === 0
    }
  },
  methods: {
    renderData () {},
    onSitesChanged () {
      let {filterSites, factorTree, query: {siteIds, factorIds}} = this

      if (filterSites.length > 0 && siteIds.length === 0) {
        siteIds.push(filterSites[0].id)
      }

      if (factorTree.length > 0 && factorIds.length === 0) {
        let firstFactorGroup = factorTree[0]
        let children = firstFactorGroup.children

        if (Array.isArray(children)) {
          let tmp = [...children]

          for (let i = 0; i < tmp.length; i++) {
            let factor = tmp[i]
            let children = factor.children

            if (Array.isArray(children)) {
              tmp.push(...children)
              continue
            }

            factorIds.push(factor.id)
          }

          this.$nextTick(() => {
            let factorTree = this.getFactorTree()

            if (factorTree) {
              factorTree.setCheckedKeys(factorIds)
            }

            factorIds = null
          })
        } else {
          factorIds.push(firstFactorGroup.id)
        }
      }

      if (!this.canNotSearch) {
        this.getData()
      }
    },
    getData () {
      let currentQuery = this.currentQuery = JSON.parse(JSON.stringify(this.query))
      this.changeLoadStatus(true)
      this.$api.Data.getStatisticAnalysisGranularityData(currentQuery).then(dataList => {
        this.buildHeaderColumns()
        this.setSiteName(this.tmpList = dataList)
        this.setDataList()
        this.renderData()
      }).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    },
    buildHeaderColumns () {
      let {currentQuery: {factorIds}, factorMap, unitProp} = this

      let headerColumns = factorIds.map(factorId => {
        let factor = factorMap[factorId]
        let displayUnit = factor[unitProp]
        return {
          id: factorId,
          name: `${factor.name}${displayUnit ? `(${displayUnit})` : ''}`,
          width: 120,
          align: 'left'
        }
      })

      headerColumns.unshift({
        id: 'dataTime',
        name: '时间',
        width: 150,
        align: 'left',
        fixed: true
      })

      this.headerColumns = Object.freeze(headerColumns)
    },
    setSiteName (dataList) {
      let {filterSiteMap} = this

      dataList.forEach(siteData => {
        siteData.siteName = filterSiteMap[siteData.siteId].name
      })
    },
    setDataList () {
      let {factorMap, currentQuery: {factorIds}, unitProp} = this
      let dataList = JSON.parse(JSON.stringify(this.tmpList))

      factorIds.forEach(factorId => {
        let factor = factorMap[factorId]
        let {stdUnit, molecular, decimalNum} = factor
        let displayUnit = factor[unitProp]

        dataList.forEach(({data}) => {
          data.forEach(data => {
            let value = data[factorId]
            data[factorId] = Number.isFinite(value) ? Number(unitConverter(value, stdUnit, displayUnit, molecular).toFixed(decimalNum)) : null
          })
        })
      })
      this.dataList = Object.freeze(dataList)
    },
    onFactorCheckChange () {
      let checkedFactorList = this.getFactorTree().getCheckedNodes()
      this.query.factorIds = checkedFactorList.filter(item => !Array.isArray(item.children)).map(item => item.id)
    },
    onUnitPropChange () {
      this.buildHeaderColumns()
      this.setDataList()
      this.renderData()
    },
    onSearchBtnClick () {
      this.getData()
    },
    onAuditCompletedChanged () {
      this.getData()
    },
    onDownloadBtnClick (data) {
      let {currentQuery: {beginTime, endTime}, headerColumns} = this
      let filename = `${data.siteName}_${new Date(beginTime).format('YYYY-MM-DD')}_${new Date(endTime).format('YYYY-MM-DD')}`
      CSVDownloader([headerColumns.map(column => column.name)].concat(data.data.map(data => headerColumns.map(column => data[column.id]))), filename)
    },
    getFactorTree () {
      return this.$refs['factor-tree']
    }
  }
}



// WEBPACK FOOTER //
// ./src/views/06Data-Management/02Normal/BaseNormal.js